/**
 * Document Save Preparation Utilities
 *
 * Centralizes conversion logic for saving document state to backend
 * NOTE: This is a lightweight wrapper that passes through data with pageId stripped for backend
 */

import { Comment } from '../CommentsSidebar';
import { Strikeout } from '../StrikeoutOverlay';
import { FreeText } from '../FreeTextOverlay';
import { UnifiedFormField } from '../utils/pdfFieldUtils';
import { FormField, PDFComment, PDFStrikeout, PDFFreeText } from '../types';

export interface PreparedDocumentData {
  formFields: FormField[];
  comments: PDFComment[];
  strikeouts: PDFStrikeout[];
  freeTexts: PDFFreeText[];
}

/**
 * Convert all document state to internal format (includes pageId)
 * The usePdfSave hook will strip pageId when sending to backend
 */
export function prepareDocumentForSave(
  allFields: UnifiedFormField[],
  comments: Comment[],
  strikeouts: Strikeout[],
  freeTexts: FreeText[]
): PreparedDocumentData {
  // Convert to internal types (which include pageId)
  const pdfStrikeouts: PDFStrikeout[] = strikeouts.map(s => ({
    id: s.id,
    pageId: s.pageId,
    pageNum: s.pageNum,
    x: s.x,
    y: s.y,
    width: s.width,
    color: s.color,
    groupId: s.groupId,
  }));

  const pdfFreeTexts: PDFFreeText[] = freeTexts.map(f => ({
    id: f.id,
    pageId: f.pageId,
    pageNum: f.pageNum,
    x: f.x,
    y: f.y,
    width: f.width,
    height: f.height,
    text: f.text,
    fontSize: f.fontSize,
    fontColor: f.fontColor,
  }));

  const formFields: FormField[] = allFields.map(f => ({
    id: f.id,
    fieldName: f.fieldName,
    type: f.type,
    pageId: f.pageId,
    pageNum: f.pageNum,
    x: f.x,
    y: f.y,
    width: f.width,
    height: f.height,
    value: f.value,
    placeholder: f.placeholder,
    label: f.placeholder,
    multiline: f.multiline,
    fontSize: f.fontSize,
    fontColor: f.fontColor,
    // Checkboxes should never have alignment properties - always centered
    horizontalAlign: f.type === 'checkbox' ? undefined : f.horizontalAlign,
    verticalAlign: f.type === 'checkbox' ? undefined : f.verticalAlign,
    required: f.required || false,
    readOnly: f.readOnly || false,
    editable: true,
  }));

  const pdfComments: PDFComment[] = comments
    .filter(c => c.text && c.text.trim() !== '')
    .map(c => ({
      id: c.id,
      pageId: c.pageId,
      pageNum: c.pageNum,
      x: c.x,
      y: c.y,
      text: c.text,
      author: c.author,
      authorId: c.authorId,
      createdAt: c.createdAt,
      parentId: c.parentId,
    }));

  return {
    formFields,
    comments: pdfComments,
    strikeouts: pdfStrikeouts,
    freeTexts: pdfFreeTexts,
  };
}
